import clsx from 'clsx';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import { useState } from 'react';
import type { ScoreInterviewContextType } from '../scoring.context';
import { useScoreInterview } from '../scoring.context';
import ScoreTableDetail from './ScoreTableDetail';

type TDetailFilter = {
  id: 'all' | 'open-ended' | 'weighted' | 'bonus';
  label: string;
};

const detailFilter: TDetailFilter[] = [
  {
    id: 'all',
    label: 'All',
  },
  {
    id: 'bonus',
    label: 'All bonus',
  },
  {
    id: 'weighted',
    label: 'All weighted',
  },
  {
    id: 'open-ended',
    label: 'All weighted open',
  },
];

const ScoreTable = () => {
  const { chat, scoreTable, scoreTableDetails } = useScoreInterview() as ScoreInterviewContextType;
  const [filterId, setFilterId] = useState<TDetailFilter['id']>('open-ended');

  if (!chat) {
    return (
      <div className="flex h-full min-h-[400px] w-full items-center justify-center text-center">
        <div className="flex flex-col items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-8 shadow-sm">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200">
            <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-700">No Chat Available.</h3>
            <p className="">
              <span className="mt-1 block text-sm font-normal text-gray-500">
                If you believe this is an error, please{' '}
                <span className="font-medium underline">contact any developer</span>.
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!scoreTable) {
    return (
      <div className="flex h-full min-h-[400px] w-full items-center justify-center text-center">
        <div className="flex flex-col items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-8 shadow-sm">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200">
            <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-700">No scoring table data available.</h3>
            <span className="">
              <span className="mt-1 block text-sm font-normal text-gray-500">
                If you believe this is an error, please{' '}
                <span className="font-medium underline">contact any developer</span>.
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('p-2', 'flex flex-col justify-start gap-4')}>
      <div className={clsx('p-2', 'flex flex-row justify-center gap-4')}>
        {detailFilter.map((filter, index) => {
          return (
            <HubertBadge
              key={`score-table-filter-${index}`}
              variant={filterId === filter.id ? 'info' : 'secondary'}
              className="cursor-pointer"
              onClick={() => {
                setFilterId(filter.id);
              }}
            >
              {filter.label}
            </HubertBadge>
          );
        })}
      </div>

      {chat.map((collection, idx: number) => {
        const details = scoreTableDetails.filter((x) => x.questionId === collection.questionId);

        if (!details || !details.length) {
          return null;
        }

        return details.map((x, index) => {
          if (filterId === 'open-ended') {
            if (x.categoryId) {
              return (
                <ScoreTableDetail
                  key={`score-table-detail-${collection._id}-${idx}-${index}`}
                  collection={collection}
                  detail={x}
                />
              );
            }
            return null;
          } else if (filterId === 'weighted') {
            if (x.type === 'weighted') {
              return (
                <ScoreTableDetail
                  key={`score-table-detail-${collection._id}-${idx}-${index}`}
                  collection={collection}
                  detail={x}
                />
              );
            }
            return null;
          }

          if (filterId === 'bonus') {
            if (x.type === 'bonus') {
              return (
                <ScoreTableDetail
                  key={`score-table-detail-${collection._id}-${idx}-${index}`}
                  collection={collection}
                  detail={x}
                />
              );
            }
            return null;
          }

          return (
            <ScoreTableDetail
              key={`score-table-detail-${collection._id}-${idx}-${index}`}
              collection={collection}
              detail={x}
            />
          );
        });
      })}
    </div>
  );
};

export default ScoreTable;

{
  /* 
{chat.map((collection, idx: number) => {
  const evaluation = collection.evaluation;

  if (evaluation && evaluation.scoreTableDetail) {
    const detail = evaluation.scoreTableDetail;

    if (Array.isArray(detail)) {
      return detail.map((scoreTableDetail, index: number) => {
        return (
          <ScoreTableDetail
            key={`score-table-detail-${collection._id}-${idx}-${index}`}
            collection={collection}
            detail={scoreTableDetail}
          />
        );
      })
    }
    return (
      <ScoreTableDetail
        key={idx}
        collection={collection}
        detail={detail}
      />
    )
  }
  return null;
})}
*/
}
